import { useState, useEffect, useRef } from 'react';

// Comprehensive validation testing function
const testValidationLogic = (profile_image, testName) => {
  console.log(`🧪 VALIDATION TEST - ${testName}:`);
  console.log(`   Input: "${profile_image}" (type: ${typeof profile_image})`);
  
  const step1 = !!profile_image;
  const step2 = profile_image && profile_image.trim() !== '';
  const step3 = profile_image !== 'null';
  const step4 = profile_image !== 'undefined';
  const result = profile_image && 
                 profile_image.trim() !== '' &&
                 profile_image !== 'null' &&
                 profile_image !== 'undefined';
  
  console.log(`   Steps: exists(${step1}) && notEmpty(${step2}) && notNull(${step3}) && notUndef(${step4}) = ${result}`);
  return result;
};

// Simple in-memory cache for profile photos
const profilePhotoCache = new Map();

const Avatar = ({ 
  user, 
  size = 'medium', 
  className = '', 
  showOnlineStatus = false,
  onClick = null 
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [authReady, setAuthReady] = useState(false);
  const retryTimeoutRef = useRef(null);
  const mountedRef = useRef(true);

  // Check if authentication is ready
  useEffect(() => {
    const checkAuthReady = () => {
      const token = localStorage.getItem('token');
      const hasAuth = !!token;
      setAuthReady(hasAuth);
    };

    // Initial check
    checkAuthReady();

    // Listen for storage changes (login/logout events)
    const handleStorageChange = () => {
      checkAuthReady();
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically in case of same-tab login
    const interval = setInterval(checkAuthReady, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
      mountedRef.current = false;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  // Size configurations
  const sizeConfig = {
    small: {
      container: 'w-8 h-8 text-xs',
      text: 'text-xs',
    },
    medium: {
      container: 'w-12 h-12 text-sm',
      text: 'text-sm',
    },
    large: {
      container: 'w-16 h-16 text-lg',
      text: 'text-lg',
    },
    xlarge: {
      container: 'w-24 h-24 text-2xl',
      text: 'text-xl',
    }
  };

  const config = sizeConfig[size] || sizeConfig.medium;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Generate initials from user name
  const getInitials = (name) => {
    if (!name) return 'U';
    const nameParts = name.trim().split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  // Generate consistent color based on user name
  const getBackgroundColor = (name) => {
    if (!name) return 'bg-gray-500';
    
    const colors = [
      'bg-blue-500',
      'bg-green-500', 
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-yellow-500',
      'bg-red-500',
      'bg-teal-500',
      'bg-orange-500',
      'bg-cyan-500'
    ];
    
    // Generate consistent color based on name hash
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const handleImageLoad = () => {
    if (!mountedRef.current) return;
    
    console.log(`✅ AVATAR DISPLAY DEBUG - Image loaded successfully for user ${user?.id}`);
    
    // Cache the successful image load
    if (user?.id) {
      const backendSize = size === 'xlarge' ? 'large' : size;
      const cacheUrl = `${backendUrl}/api/users/profile/photo/${user.id}/${backendSize}`;
      profilePhotoCache.set(user.id, {
        url: cacheUrl,
        timestamp: Date.now(),
        size: backendSize
      });
      console.log(`💾 Cached avatar image: ${cacheUrl}`);
    }
    
    setImageLoading(false);
    setImageError(false);
    setRetryCount(0);
    console.log(`🎨 Avatar display state updated: loading=false, error=false, retryCount=0`);
  };

  const handleImageError = (e) => {
    if (!mountedRef.current) return;

    console.log(`❌ AVATAR DISPLAY DEBUG - Image load failed for user ${user?.id}`);
    console.log(`🔍 Error event details:`, {
      type: e.type,
      target_src: e.target.src,
      target_naturalWidth: e.target.naturalWidth,
      target_naturalHeight: e.target.naturalHeight,
      user_profile_image: user?.profile_image
    });

    // For profile photos, most errors are "no photo available" rather than network issues
    // The key insight: if we're trying to load a profile photo and it fails, 
    // it's almost certainly because the user has no profile photo uploaded
    
    const isAuthError = e.target.src && (e.target.src.includes('401') || e.target.src.includes('403'));
    const isBrowserExtensionError = e.type === 'error' && !e.target.src;
    
    // Check if this is a profile photo request (contains /profile/photo/ in URL)
    const isProfilePhotoRequest = e.target.src && e.target.src.includes('/profile/photo/');
    
    console.log(`🔍 Error classification:`, {
      isAuthError,
      isBrowserExtensionError,
      isProfilePhotoRequest
    });
    
    // For profile photo requests, treat any error as "no photo available" 
    // This includes 404, 405, network errors, etc.
    if (isProfilePhotoRequest && !isAuthError && !isBrowserExtensionError) {
      console.log(`📷 Profile photo not available for user ${user?.id} - showing initials fallback`);
      console.log(`🔍 Failed URL was: ${e.target.src}`);
      setImageLoading(false);
      setImageError(true);
      return;
    }
    
    // For non-profile photo requests, use original logic
    const isNetworkError = e.target.naturalWidth === 0 && e.target.naturalHeight === 0;
    const isImageLoadFailure = !isNetworkError && !isAuthError && !isBrowserExtensionError;

    console.log(`🔍 Fallback error classification:`, {
      isNetworkError,
      isImageLoadFailure
    });

    // Handle other image load failures gracefully - don't retry
    if (isImageLoadFailure) {
      console.log(`📷 Image load failed for user ${user?.id} - showing initials fallback`);
      setImageLoading(false);
      setImageError(true);
      return;
    }

    console.warn(`Avatar image load failed for user ${user?.id}:`, {
      isNetworkError,
      isAuthError,
      isBrowserExtensionError,
      retryCount,
      src: e.target.src
    });

    // Handle browser extension errors gracefully
    if (isBrowserExtensionError) {
      console.log('Detected browser extension interference, falling back to initials');
      setImageLoading(false);
      setImageError(true);
      return;
    }

    // Retry logic for network/auth errors only
    if (retryCount < 3 && (isNetworkError || isAuthError)) {
      const delay = Math.min(1000 * Math.pow(2, retryCount), 5000); // Exponential backoff, max 5s
      
      setRetryCount(prev => prev + 1);
      
      retryTimeoutRef.current = setTimeout(() => {
        if (!mountedRef.current) return;
        
        console.log(`Retrying avatar image load (attempt ${retryCount + 1}/3) for user ${user?.id}`);
        
        // Force reload by adding timestamp
        const originalSrc = e.target.src.split('?')[0];
        e.target.src = `${originalSrc}?retry=${Date.now()}`;
      }, delay);
    } else {
      // Max retries reached or non-retryable error
      setImageLoading(false);
      setImageError(true);
      
      // Remove from cache if it exists
      if (user?.id) {
        profilePhotoCache.delete(user.id);
      }
    }
  };

  // Reset states when user changes
  useEffect(() => {
    console.log(`🔬 VALIDATION DEBUG - useEffect triggered for user:`, user?.id);
    
    if (user?.id) {
      // Comprehensive validation debugging
      console.log(`🔬 VALIDATION DEBUG - Detailed validation analysis for user ${user.id}:`);
      console.log(`   📄 Raw user object:`, user);
      console.log(`   🖼️ profile_image value:`, user.profile_image);
      console.log(`   🔍 profile_image type:`, typeof user.profile_image);
      console.log(`   📏 profile_image length:`, user.profile_image ? user.profile_image.length : 'N/A');
      console.log(`   💻 profile_image charCodes:`, user.profile_image ? Array.from(user.profile_image).map(char => char.charCodeAt(0)).slice(0, 20) : 'N/A');
      
      // Step-by-step validation
      const step1_exists = !!user?.profile_image;
      console.log(`   ✅ Step 1 - profile_image exists:`, step1_exists, user?.profile_image);
      
      const step2_truthy = user?.profile_image && true;
      console.log(`   ✅ Step 2 - profile_image is truthy:`, step2_truthy);
      
      const trimmed = user?.profile_image ? user.profile_image.trim() : '';
      console.log(`   ✅ Step 3 - trimmed value:`, trimmed, `(length: ${trimmed.length})`);
      
      const step3_notEmpty = trimmed !== '';
      console.log(`   ✅ Step 4 - trimmed not empty:`, step3_notEmpty);
      
      const step4_notNull = user?.profile_image !== 'null';
      console.log(`   ✅ Step 5 - not string 'null':`, step4_notNull);
      
      const step5_notUndefined = user?.profile_image !== 'undefined';
      console.log(`   ✅ Step 6 - not string 'undefined':`, step5_notUndefined);
      
      // Final validation result
      const hasValidProfileImage = user?.profile_image && 
                                   user.profile_image.trim() !== '' &&
                                   user.profile_image !== 'null' &&
                                   user.profile_image !== 'undefined';
      
      console.log(`   🎯 FINAL VALIDATION RESULT:`, hasValidProfileImage);
      console.log(`   🧮 Calculation: ${step1_exists} && ${step3_notEmpty} && ${step4_notNull} && ${step5_notUndefined} = ${hasValidProfileImage}`);
      
      // Run comprehensive test with the actual value
      const testResult = testValidationLogic(user.profile_image, `User ${user.id} Validation`);
      console.log(`   🧪 Test function result: ${testResult}`);
      console.log(`   🔍 Validation mismatch:`, hasValidProfileImage !== testResult ? 'YES - ISSUE FOUND!' : 'No');
      
      // Run additional test cases to verify logic
      console.log(`🧪 COMPREHENSIVE VALIDATION TESTS:`);
      testValidationLogic('/api/users/profile/photo/test/medium', 'Known good API path');
      testValidationLogic('', 'Empty string');
      testValidationLogic(null, 'Null value');
      testValidationLogic(undefined, 'Undefined value');
      testValidationLogic('null', 'String null');
      testValidationLogic('undefined', 'String undefined');
      testValidationLogic('   ', 'Whitespace only');
      testValidationLogic('https://example.com/image.jpg', 'Full URL');
      
      // Debug logging to understand user object structure
      console.log(`🔍 Avatar component processing user ${user.id}:`, {
        id: user.id,
        profile_image: user.profile_image,
        full_name: user.full_name,
        username: user.username,
        hasValidProfileImage: hasValidProfileImage
      });
      
      // Only reset loading state if user has a valid profile image
      if (hasValidProfileImage) {
        console.log(`🔄 User ${user.id} has valid profile image - preparing to load`);
        setImageError(false);
        setImageLoading(true);
        setRetryCount(0);
        
        // Clear any pending retry
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }
      } else {
        // User has no valid profile photo - show initials immediately
        console.log(`📷 User ${user.id} has no valid profile image - showing initials`);
        setImageError(true);
        setImageLoading(false);
        setRetryCount(0);
        
        // Clear any pending retry
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }
      }
    } else {
      console.log(`🔬 VALIDATION DEBUG - No user or user ID provided`);
    }
  }, [user?.id, user?.profile_image]);

  // Check if user has a profile image - API paths are now valid after upload
  // Only exclude empty values or obviously invalid paths
  const hasValidProfileImage = user?.profile_image && 
                               user.profile_image.trim() !== '' &&
                               user.profile_image !== 'null' &&
                               user.profile_image !== 'undefined';
  
  console.log(`🔬 DISPLAY VALIDATION DEBUG - Main validation check:`, {
    user_id: user?.id,
    profile_image: user?.profile_image,
    hasValidProfileImage: hasValidProfileImage,
    imageError: imageError,
    authReady: authReady
  });
  
  const shouldShowImage = hasValidProfileImage && !imageError && authReady;
  
  console.log(`🔬 DISPLAY VALIDATION DEBUG - shouldShowImage result:`, shouldShowImage);
  const initials = getInitials(user?.full_name || user?.username || 'User');
  const bgColor = getBackgroundColor(user?.full_name || user?.username || 'User');

  // Map frontend sizes to backend sizes
  const backendSize = size === 'xlarge' ? 'large' : size;

  // Check cache first
  const cachedImage = user?.id ? profilePhotoCache.get(user.id) : null;
  const isCacheValid = cachedImage && 
    cachedImage.size === backendSize && 
    (Date.now() - cachedImage.timestamp) < 300000; // 5 minutes cache

  return (
    <div 
      className={`relative inline-block ${onClick ? 'cursor-pointer' : ''} ${className}`}
      onClick={onClick}
    >
      <div
        className={`
          ${config.container} 
          rounded-full 
          flex 
          items-center 
          justify-center 
          font-medium 
          text-white 
          overflow-hidden 
          ${shouldShowImage ? 'bg-gray-200' : bgColor}
          ${onClick ? 'hover:shadow-lg transition-shadow' : ''}
        `}
      >
        {shouldShowImage ? (
          <>
            <img
              src={(() => {
                let finalUrl;
                const timestamp = Date.now();
                
                if (isCacheValid) {
                  finalUrl = cachedImage.url;
                  console.log(`🎯 AVATAR URL DEBUG - Using cached URL: ${finalUrl}`);
                } else if (user.profile_image.startsWith('/api/')) {
                  finalUrl = `${backendUrl}${user.profile_image}?t=${timestamp}`;
                  console.log(`🎯 AVATAR URL DEBUG - Using API path: ${finalUrl}`);
                } else if (user.profile_image.startsWith('http')) {
                  finalUrl = user.profile_image;
                  console.log(`🎯 AVATAR URL DEBUG - Using full URL: ${finalUrl}`);
                } else {
                  finalUrl = `${backendUrl}/api/users/profile/photo/${user.id}/${backendSize}?t=${timestamp}`;
                  console.log(`🎯 AVATAR URL DEBUG - Using constructed URL: ${finalUrl}`);
                }
                
                return finalUrl;
              })()}
              alt={user.full_name || user.username || 'User'}
              className={`w-full h-full object-cover ${imageLoading ? 'opacity-0' : 'opacity-100'} transition-opacity`}
              onLoad={handleImageLoad}
              onError={handleImageError}
              loading="lazy"
            />
            {imageLoading && (
              <div className={`absolute inset-0 flex items-center justify-center ${bgColor}`}>
                {retryCount > 0 ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <span className={`${config.text} font-medium text-white`}>
                    {initials}
                  </span>
                )}
              </div>
            )}
          </>
        ) : (
          <span className={`${config.text} font-medium`}>
            {initials}
          </span>
        )}
      </div>

      {/* Online status indicator */}
      {showOnlineStatus && (
        <div className="absolute -bottom-0.5 -right-0.5">
          <div className="w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>
        </div>
      )}
    </div>
  );
};

export default Avatar;